/**
 * EnvironmentDetector - Utility class for detecting the current environment
 * 
 * Detection priority order:
 * 1. REACT_APP_ENV environment variable
 * 2. NODE_ENV environment variable (production → production, others → development)
 * 3. URL-based detection (hostname analysis)
 * 4. Default fallback to 'unknown'
 */
class EnvironmentDetector {
  /**
   * Detect the current environment
   * @returns {string} The detected environment: 'development', 'staging', 'production', or 'unknown'
   */
  static detectEnvironment() {
    // Priority 1: REACT_APP_ENV
    if (process.env.REACT_APP_ENV && process.env.REACT_APP_ENV.trim() !== '') {
      const validated = this.validateEnvironment(process.env.REACT_APP_ENV);
      if (validated !== 'unknown') {
        return validated;
      }
    }

    // Priority 2: NODE_ENV
    if (process.env.NODE_ENV && process.env.NODE_ENV.trim() !== '') {
      if (process.env.NODE_ENV === 'production') {
        return 'production';
      }
      return 'development';
    }

    // Priority 3: URL-based detection
    const urlEnvironment = this.detectFromURL();
    if (urlEnvironment) {
      return urlEnvironment;
    }

    // Priority 4: Default fallback
    return 'unknown';
  }

  /**
   * Detect environment from URL hostname
   * @returns {string|null} The detected environment or null if cannot determine
   */
  static detectFromURL() {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || !window.location || !window.location.hostname) {
      return null;
    }

    const hostname = window.location.hostname;

    // Handle empty or invalid hostnames
    if (!hostname || hostname.trim() === '') {
      return null;
    }

    // Development environments
    if (hostname.includes('localhost') || 
        hostname.includes('127.0.0.1') || 
        hostname.includes('0.0.0.0')) {
      return 'development';
    }

    // Staging environments
    if (hostname.includes('staging') || 
        hostname.includes('stage') ||
        hostname.includes('dev.') ||
        hostname.includes('-dev.') ||
        hostname.includes('test.')) {
      return 'staging';
    }

    // Production environments (everything else with a proper domain)
    if (hostname.includes('.') && !hostname.includes('localhost')) {
      return 'production';
    }

    return null;
  }

  /**
   * Get detailed detection metadata for debugging
   * @returns {object} Detection metadata including method used and context
   */
  static getDetectionMetadata() {
    const metadata = {
      detected_environment: this.detectEnvironment(),
      detection_method: null,
      timestamp: new Date().toISOString(),
      context: {}
    };

    // Determine which method was used
    if (process.env.REACT_APP_ENV) {
      metadata.detection_method = 'env_var';
      metadata.context.react_app_env = process.env.REACT_APP_ENV;
    } else if (process.env.NODE_ENV) {
      metadata.detection_method = 'node_env';
      metadata.context.node_env = process.env.NODE_ENV;
    } else if (this.detectFromURL()) {
      metadata.detection_method = 'url';
      if (typeof window !== 'undefined') {
        metadata.context.hostname = window.location.hostname;
        metadata.context.href = window.location.href;
      }
    } else {
      metadata.detection_method = 'default';
    }

    // Add additional context
    metadata.context.user_agent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    metadata.context.available_env_vars = {
      react_app_env: !!process.env.REACT_APP_ENV,
      node_env: !!process.env.NODE_ENV
    };

    return metadata;
  }

  /**
   * Validate and normalize environment string
   * @param {string} env - Environment string to validate
   * @returns {string} Validated environment string
   */
  static validateEnvironment(env) {
    if (!env || typeof env !== 'string') {
      return 'unknown';
    }

    const normalizedEnv = env.toLowerCase().trim();
    const validEnvironments = ['development', 'staging', 'production'];

    if (validEnvironments.includes(normalizedEnv)) {
      return normalizedEnv;
    }

    // Handle common variations
    if (normalizedEnv === 'dev') {
      return 'development';
    }
    if (normalizedEnv === 'stage') {
      return 'staging';
    }
    if (normalizedEnv === 'prod') {
      return 'production';
    }

    return 'unknown';
  }

  /**
   * Check if current environment is production
   * @returns {boolean} True if environment is production
   */
  static isProduction() {
    return this.detectEnvironment() === 'production';
  }

  /**
   * Check if current environment is development
   * @returns {boolean} True if environment is development
   */
  static isDevelopment() {
    return this.detectEnvironment() === 'development';
  }

  /**
   * Check if current environment is staging
   * @returns {boolean} True if environment is staging
   */
  static isStaging() {
    return this.detectEnvironment() === 'staging';
  }
}

export default EnvironmentDetector;